import { getUserDetails, makeGetCall, makePostCall } from './helper';

/**
 * Post-commit report preview.
 *
 * After a Draft / Reviewed / Sign off, the reporting page can hold the just-committed
 * report on screen read-only for a few seconds before the queue closes the window or
 * advances to the next study. Radiologists working a long queue mostly consider that a
 * few wasted seconds per study, so the preview is OFF unless someone asks for it.
 *
 * Resolution order for any given user:
 *   1. their own choice, saved in `user_preferences` (up_type = 'report_preview')
 *   2. the centre default from window.ENV_CONFIG (REPORT_PREVIEW_ENABLED / _SECONDS)
 *   3. the compiled-in default below — off, 5 seconds if switched on
 *
 * A user's choice is a tri-state on purpose: 'default' is not the same as 'off', it means
 * "follow whatever the centre is configured for", so flipping the centre flag still
 * reaches everyone who never expressed a preference of their own.
 */
export type ReportPreviewMode = 'default' | 'on' | 'off';

/** What the user picked in Edit Profile (the raw, unresolved choice). */
export interface ReportPreviewChoice {
  mode: ReportPreviewMode;
  /** Hold duration for mode 'on'. Kept even while 'default'/'off' so toggling back
   *  restores the number the user last typed instead of resetting it. */
  seconds: number;
}

/** What the reporting page actually acts on, after the fallbacks are applied. */
export interface ReportPreviewSetting {
  enabled: boolean;
  seconds: number;
}

export const MIN_PREVIEW_SECONDS = 1;
export const MAX_PREVIEW_SECONDS = 60;

/** Used when neither the user nor ENV_CONFIG says anything. */
const FALLBACK_ENABLED = false;
const FALLBACK_SECONDS = 5;

export const DEFAULT_REPORT_PREVIEW_CHOICE: ReportPreviewChoice = {
  mode: 'default',
  seconds: FALLBACK_SECONDS,
};

const PREFERENCE_TYPE = 'report_preview';

/**
 * localStorage is ONLY a synchronous cache of the row above, never the source of truth —
 * same contract as the workstation layout preference. The setting is read at commit time
 * inside a promise callback where awaiting a round-trip would stall the handoff, and the
 * reporting page runs in its own window that must honour a change made in the PACS tab
 * without a reload (localStorage is shared across tabs of the same origin, so it does).
 *
 * Keyed by username so a shared workstation can't serve one radiologist's setting to the
 * next person who logs in. Logout clears localStorage; preloadReportPreview() refills it
 * on the next boot, which is a full page load after every login.
 */
const STORAGE_KEY = 'pacs_report_preview';

const currentUsername = (): string => {
  try {
    const user = getUserDetails();
    return user?.username || user?.user_name || '';
  } catch {
    return '';
  }
};

export const clampSeconds = (value: any): number => {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n)) return FALLBACK_SECONDS;
  return Math.min(MAX_PREVIEW_SECONDS, Math.max(MIN_PREVIEW_SECONDS, n));
};

/**
 * The centre default. Read from window.ENV_CONFIG on every call rather than captured at
 * module load, so it reflects the deployed env-config.js even for code paths that run
 * before / independently of the app bundle's own config plumbing.
 */
export const getReportPreviewDefault = (): ReportPreviewSetting => {
  const cfg = (window as any).ENV_CONFIG || {};
  return {
    enabled: cfg.REPORT_PREVIEW_ENABLED === true,
    seconds:
      cfg.REPORT_PREVIEW_SECONDS === undefined || cfg.REPORT_PREVIEW_SECONDS === null
        ? FALLBACK_SECONDS
        : clampSeconds(cfg.REPORT_PREVIEW_SECONDS),
  };
};

const asChoice = (value: any): ReportPreviewChoice | null => {
  if (!value || typeof value !== 'object') return null;
  const mode: ReportPreviewMode =
    value.mode === 'on' || value.mode === 'off' || value.mode === 'default'
      ? value.mode
      : 'default';
  return { mode, seconds: clampSeconds(value.seconds ?? getReportPreviewDefault().seconds) };
};

const readCache = (): Record<string, ReportPreviewChoice> => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    // Corrupt JSON, or storage blocked on a locked-down browser profile.
    return {};
  }
};

const writeCache = (username: string, choice: ReportPreviewChoice): void => {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...readCache(), [username]: choice })
    );
  } catch (e) {
    console.warn('reportPreviewPref: failed to write localStorage cache', e);
  }
};

/** The user's raw choice — what Edit Profile renders. 'default' when they never set one. */
export const getReportPreviewChoice = (): ReportPreviewChoice => {
  const username = currentUsername();
  if (!username) return { ...DEFAULT_REPORT_PREVIEW_CHOICE, seconds: getReportPreviewDefault().seconds };

  return (
    asChoice(readCache()[username]) || {
      ...DEFAULT_REPORT_PREVIEW_CHOICE,
      seconds: getReportPreviewDefault().seconds,
    }
  );
};

/** The resolved setting the reporting page acts on. Synchronous, safe inside a callback. */
export const getReportPreviewSetting = (): ReportPreviewSetting => {
  const choice = getReportPreviewChoice();
  const fallback = getReportPreviewDefault();

  if (choice.mode === 'default') return fallback;
  return { enabled: choice.mode === 'on', seconds: clampSeconds(choice.seconds) };
};

/**
 * Hydrate the cache from the database. Called from index.js before the app mounts, so the
 * first commit already reads the stored value. Safe no-op when nobody is logged in.
 */
export const preloadReportPreview = async (): Promise<ReportPreviewChoice | null> => {
  const username = currentUsername();
  if (!username) return null;

  try {
    const resp = await makeGetCall(
      `/get-user-preference?username=${encodeURIComponent(username)}&type=${PREFERENCE_TYPE}`
    );
    const choice = asChoice(resp?.data?.data);

    // A successful fetch is authoritative: mirror exactly what the row holds (the
    // 'default' choice when there is no row yet) so a preference cleared server-side
    // cannot linger here.
    writeCache(username, choice || {
      ...DEFAULT_REPORT_PREVIEW_CHOICE,
      seconds: getReportPreviewDefault().seconds,
    });
    return choice;
  } catch (e) {
    // Offline or the endpoint failed — keep whatever the cache already holds.
    console.warn('reportPreviewPref: preload failed, using cached/default setting', e);
    return null;
  }
};

/**
 * Persist the choice. Writes the cache first so the very next commit honours the new value
 * even while the request is still in flight. Resolves false when the row could not be
 * saved, so the caller can tell the user it will not follow them to another machine.
 */
export const setReportPreviewChoice = async (choice: ReportPreviewChoice): Promise<boolean> => {
  const username = currentUsername();
  if (!username) return false;

  const value: ReportPreviewChoice = {
    mode: choice.mode,
    seconds: clampSeconds(choice.seconds),
  };

  writeCache(username, value);

  try {
    await makePostCall('/save-user-preference', {
      username,
      type: PREFERENCE_TYPE,
      value,
    });
    return true;
  } catch (e) {
    console.error('reportPreviewPref: failed to persist preference', e);
    return false;
  }
};

/** Only reporting roles ever see the post-commit preview, so only they get the setting. */
export const canConfigureReportPreview = (userDetails: any): boolean =>
  ['radiologist', 'typist', 'hod'].includes(userDetails?.user_type);
